// Kill Sheet Engine — input validation (P3 of Kill Sheet Redev).
//
// Runs BEFORE compose, never during. If validation rejects a session, the
// composer refuses to run and returns a structured error report. This is a
// hard guard against impossible-physics inputs sneaking into a printed
// kill sheet.
//
// All checks are pure: they take a fully-resolved session (profiles already
// snapshotted into the session object) and return:
//   { ok: bool, errors: [{code, message, path}], warnings: [...] }
//
// Errors block composition. Warnings do not block but are surfaced on the
// preview screen and in the printed sheet's footer.

const _isNum  = (v) => v !== "" && v !== null && v !== undefined && Number.isFinite(parseFloat(v));
const _toNum  = (v) => parseFloat(v);
const _posNum = (v) => _isNum(v) && _toNum(v)  >  0;
const _nonNeg = (v) => _isNum(v) && _toNum(v) >=  0;

const _push = (list, code, message, path) =>
  list.push({ code, message, path });

// -----------------------------------------------------------------------------
// validateSession — top-level checker.
// session: {
//   variant, units,
//   well:   { ...WellProfile.payload },
//   bha:    { ...BHAProfile.payload },
//   casing: { ...CasingProfile.payload },
//   mud:    { ...MudProfile.payload },
//   pump:   { selectedIndex, ...PumpProfile.payload },
//   capacities: { dpInternal, hwdpInternal, dcInternal, dpHwdpInOpenHole,
//                 dcInOpenHole, dpInCasing, dcInCasing },
//   lengths:    { dp, hwdp, dc, dpHwdpOpenHole, dcOpenHole, dpCasing, dcCasing },
//   kickData:   { sidpp, sicp, pitGain }
// }
// -----------------------------------------------------------------------------
export function validateSession(session) {
  const errors   = [];
  const warnings = [];

  if (!session || typeof session !== "object") {
    _push(errors, "E_SESSION_MISSING", "Session payload missing.", "session");
    return { ok: false, errors, warnings };
  }
  if (!["IMP", "SI"].includes(session.units)) {
    _push(errors, "E_UNITS_INVALID", `Units must be 'IMP' or 'SI' (got '${session.units}').`, "units");
  }
  // Soft warning: each bound profile must declare the same units as the
  // kill sheet itself, otherwise the auto-derivation layer mixes IMP and SI
  // formulas silently. We surface this in the editor's warning panel
  // (user-chosen behaviour: option C — allow bind, soft warn only).
  ["well", "bha", "casing", "mud", "pump", "opstate"].forEach((kind) => {
    const prof = session[kind];
    if (prof && prof.units && session.units && prof.units !== session.units) {
      const label = session.units === "SI" ? "Metric" : "Imperial";
      const other = prof.units === "SI" ? "Metric" : "Imperial";
      _push(warnings, "W_UNITS_MISMATCH",
        `${kind.toUpperCase()} profile is ${other} but kill sheet is ${label}. ` +
        `Auto-derivation may mix unit systems — review derived values.`,
        kind);
    }
  });
  const VARIANTS = ["surface-vertical", "surface-horizontal", "subsea-vertical", "subsea-horizontal"];
  if (!VARIANTS.includes(session.variant)) {
    _push(errors, "E_VARIANT_INVALID", `Variant must be one of ${VARIANTS.join(", ")} (got '${session.variant}').`, "variant");
  }

  _validateWell    (session.well   || {}, errors, warnings);
  _validateBHA     (session.bha    || {}, errors, warnings);
  _validateCasing  (session.casing || {}, errors, warnings);
  _validateMud     (session.mud    || {}, errors, warnings);
  _validatePump    (session.pump   || {}, errors, warnings);
  _validateCapacities(session.capacities || {}, session.lengths || {}, errors, warnings);
  _validateLengths (session.lengths || {}, session.well || {}, errors, warnings);
  _validateKickData(session.kickData || {}, session.well || {}, errors, warnings);

  // Cross-section consistency
  _validateCrossSection(session, errors, warnings);

  if (session.variant && session.variant.startsWith("subsea-")) {
    _validateSubsea(session.well || {}, errors, warnings);
  }

  return { ok: errors.length === 0, errors, warnings };
}

// -----------------------------------------------------------------------------
function _validateWell(well, errors, warnings) {
  if (!_posNum(well.currentMd))   _push(errors, "E_WELL_MD",       "Current MD must be > 0.", "well.currentMd");
  if (!_posNum(well.currentTvd))  _push(errors, "E_WELL_TVD",      "Current TVD must be > 0.", "well.currentTvd");
  if (_isNum(well.currentMd) && _isNum(well.currentTvd) &&
      _toNum(well.currentTvd) > _toNum(well.currentMd)) {
    _push(errors, "E_TVD_GT_MD",
      `TVD (${well.currentTvd}) cannot exceed MD (${well.currentMd}).`, "well.currentTvd");
  }
  if (!_posNum(well.shoeMd))   _push(errors, "E_SHOE_MD",   "Shoe MD must be > 0.", "well.shoeMd");
  if (!_posNum(well.shoeTvd))  _push(errors, "E_SHOE_TVD",  "Shoe TVD must be > 0.", "well.shoeTvd");
  if (_isNum(well.shoeMd) && _isNum(well.currentMd) &&
      _toNum(well.shoeMd) > _toNum(well.currentMd)) {
    _push(errors, "E_SHOE_GT_MD",
      `Shoe MD (${well.shoeMd}) cannot exceed Current MD (${well.currentMd}).`, "well.shoeMd");
  }
  if (_isNum(well.shoeTvd) && _isNum(well.currentTvd) &&
      _toNum(well.shoeTvd) > _toNum(well.currentTvd)) {
    _push(errors, "E_SHOE_TVD_GT_TVD",
      `Shoe TVD (${well.shoeTvd}) cannot exceed Current TVD (${well.currentTvd}).`, "well.shoeTvd");
  }
  if (!_posNum(well.holeSize)) _push(errors, "E_HOLE_SIZE", "Hole size must be > 0.", "well.holeSize");

  // LOT data is required to compute MAASP. If missing → warn only (the
  // composer will produce maasp = null and the PDF will print "—").
  if (!_posNum(well.lotPressure)) {
    _push(warnings, "W_NO_LOT",
      "No formation-strength-test (LOT) pressure on Well Profile — MAASP cannot be computed.",
      "well.lotPressure");
  }
  if (!_posNum(well.lotTestMudDensity)) {
    _push(warnings, "W_NO_LOT_MW",
      "No mud density at LOT test on Well Profile — MAASP cannot be computed.",
      "well.lotTestMudDensity");
  }
}

function _validateBHA(bha, errors, warnings) {
  if (!Array.isArray(bha.components) || bha.components.length === 0) {
    _push(errors, "E_BHA_EMPTY", "BHA must contain at least one component.", "bha.components");
    return;
  }
  bha.components.forEach((c, i) => {
    if (!_posNum(c.length))
      _push(errors, "E_BHA_LEN", `BHA row ${i + 1}: length must be > 0.`, `bha.components[${i}].length`);
  });
}

function _validateCasing(csg, errors, warnings) {
  if (!Array.isArray(csg.strings) || csg.strings.length === 0) {
    _push(errors, "E_CSG_EMPTY", "Casing profile must contain at least one string.", "casing.strings");
    return;
  }
  csg.strings.forEach((s, i) => {
    if (!_posNum(s.odIn)) _push(errors, "E_CSG_OD", `Casing row ${i + 1}: OD must be > 0.`, `casing.strings[${i}].odIn`);
    if (!_posNum(s.idIn)) _push(errors, "E_CSG_ID", `Casing row ${i + 1}: ID must be > 0.`, `casing.strings[${i}].idIn`);
    if (_isNum(s.odIn) && _isNum(s.idIn) && _toNum(s.idIn) >= _toNum(s.odIn)) {
      _push(errors, "E_CSG_ID_GE_OD",
        `Casing row ${i + 1}: ID (${s.idIn}) must be less than OD (${s.odIn}).`,
        `casing.strings[${i}].idIn`);
    }
  });
}

function _validateMud(mud, errors, warnings) {
  if (!_posNum(mud.density)) {
    _push(errors, "E_MUD_DENSITY",
      "Mud density must be > 0.  Negative or zero density is physically impossible.",
      "mud.density");
  }
  if (mud.gradient !== undefined && mud.gradient !== "" && !_posNum(mud.gradient)) {
    _push(errors, "E_MUD_GRADIENT", "Mud gradient, if entered, must be > 0.", "mud.gradient");
  }
  if (mud.activeSurfaceVolume !== undefined && mud.activeSurfaceVolume !== "" && !_posNum(mud.activeSurfaceVolume)) {
    _push(errors, "E_MUD_SURF_VOL",
      "Active surface volume, if entered, must be > 0.", "mud.activeSurfaceVolume");
  }
  if (!_posNum(mud.activeSurfaceVolume)) {
    _push(warnings, "W_NO_SURF_VOL",
      "No active surface volume on Mud Profile — Surface-Line strokes will print as '—'.",
      "mud.activeSurfaceVolume");
  }
}

function _validatePump(pump, errors, warnings) {
  if (!Array.isArray(pump.pumps) || pump.pumps.length === 0) {
    _push(errors, "E_PUMP_EMPTY", "Pump profile must contain at least one pump.", "pump.pumps");
    return;
  }
  const idx = Number.isInteger(pump.selectedIndex) ? pump.selectedIndex : 0;
  if (idx < 0 || idx >= pump.pumps.length) {
    _push(errors, "E_PUMP_INDEX",
      `Selected pump index ${idx} is out of range (0–${pump.pumps.length - 1}).`,
      "pump.selectedIndex");
    return;
  }
  const p = pump.pumps[idx];
  if (!_posNum(p.displacement)) {
    _push(errors, "E_PUMP_DISP",
      "Pump displacement must be > 0. Zero displacement gives infinite stroke counts.",
      `pump.pumps[${idx}].displacement`);
  }
  if (!_posNum(p.spm)) {
    _push(errors, "E_PUMP_SPM",
      "Slow pump rate (SPM) must be > 0. Required for circulation time.",
      `pump.pumps[${idx}].spm`);
  }
  if (!_posNum(p.slowPumpPressure)) {
    _push(errors, "E_PUMP_SCR",
      "Slow pump pressure (dynamic pressure loss) must be > 0. Required for ICP/FCP.",
      `pump.pumps[${idx}].slowPumpPressure`);
  }
}

// A capacity is only required when its tubular section actually exists
// (corresponding length > 0). A BHA legitimately has no Drill Collars, or
// no HWDP -- composer.js's mkSection() already renders an absent section
// as "--" (null volume, never a fabricated one) when length is 0, so this
// must not block Kill Sheet creation over a section that simply isn't
// there. Drill Pipe is the one section that's always present, so its
// capacity stays unconditionally required.
const _CAPACITY_LENGTH_KEY = {
  dpInternal:       null,
  hwdpInternal:     "hwdp",
  dcInternal:       "dc",
  dpHwdpInOpenHole: "dpHwdpOpenHole",
  dcInOpenHole:     "dcOpenHole",
  dpInCasing:       "dpInCasing",
};

function _validateCapacities(caps, lens, errors, warnings) {
  for (const [capKey, lenKey] of Object.entries(_CAPACITY_LENGTH_KEY)) {
    const sectionExists = lenKey === null || _posNum(lens[lenKey]);
    if (!sectionExists) continue; // length 0 -- section absent, capacity is N/A
    if (!_posNum(caps[capKey])) {
      _push(errors, "E_CAP_MISSING",
        `Capacity '${capKey}' must be > 0.  Compute from tubular OD/ID or use library defaults.`,
        `capacities.${capKey}`);
    }
  }
}

function _validateLengths(lens, well, errors, warnings) {
  const required = ["dp", "hwdp", "dc", "dpHwdpOpenHole", "dcOpenHole", "dpInCasing"];
  for (const k of required) {
    if (lens[k] === undefined || lens[k] === "" || _toNum(lens[k]) < 0) {
      _push(errors, "E_LEN_MISSING",
        `Length '${k}' must be ≥ 0.`,
        `lengths.${k}`);
    }
  }
}

function _validateKickData(k, well, errors, warnings) {
  if (k.sidpp === undefined || k.sidpp === "" || _toNum(k.sidpp) < 0) {
    _push(errors, "E_SIDPP_NEG",
      "SIDPP cannot be negative.", "kickData.sidpp");
  }
  if (k.sicp === undefined || k.sicp === "" || _toNum(k.sicp) < 0) {
    _push(errors, "E_SICP_NEG",
      "SICP cannot be negative.", "kickData.sicp");
  }
  if (k.pitGain === undefined || k.pitGain === "" || _toNum(k.pitGain) < 0) {
    _push(errors, "E_PG_NEG",
      "Pit gain cannot be negative.", "kickData.pitGain");
  }
  // Operational sanity warnings (not blockers).
  if (_isNum(k.sidpp) && _isNum(k.sicp)) {
    const sidpp = _toNum(k.sidpp), sicp = _toNum(k.sicp);
    if (sidpp > sicp) {
      _push(warnings, "W_SIDPP_GT_SICP",
        `SIDPP (${sidpp}) > SICP (${sicp}). Verify — for a typical gas kick SICP ≥ SIDPP.`,
        "kickData.sidpp");
    }
  }
}

function _validateCrossSection(session, errors, warnings) {
  // BHA total length should be ≤ Current MD.
  const md = _toNum(session.well?.currentMd);
  const bhaLen = Array.isArray(session.bha?.components)
    ? session.bha.components.reduce((sum, c) =>
        sum + (_isNum(c.length) ? _toNum(c.length) : 0), 0)
    : 0;
  if (_isNum(md) && bhaLen > 0 && bhaLen > md) {
    _push(errors, "E_BHA_GT_MD",
      `Total BHA length (${bhaLen}) cannot exceed Current MD (${md}).`,
      "bha.components");
  }
  // Sum of section lengths should be ≤ MD.
  const lens = session.lengths || {};
  const dpHwdpDc = ["dp", "hwdp", "dc"].reduce((s, k) =>
    s + (_isNum(lens[k]) ? _toNum(lens[k]) : 0), 0);
  if (_isNum(md) && dpHwdpDc > md * 1.0001 /* tiny tolerance */) {
    _push(warnings, "W_LEN_SUM_GT_MD",
      `Sum of drill-string section lengths (${dpHwdpDc.toFixed(1)}) exceeds Current MD (${md}). Re-check your tally.`,
      "lengths");
  }
}

function _validateSubsea(well, errors, warnings) {
  if (!well.isSubsea) {
    _push(warnings, "W_SUBSEA_PROFILE_MISMATCH",
      "Variant is subsea but the chosen Well Profile is not flagged as subsea. Riser/choke-line data will not contribute to volumes.",
      "well.isSubsea");
  }
  if (well.isSubsea) {
    if (!_posNum(well.waterDepth))
      _push(warnings, "W_NO_WATER_DEPTH", "Subsea variant: water depth missing.", "well.waterDepth");
    if (!_posNum(well.riserIdIn))
      _push(warnings, "W_NO_RISER_ID",    "Subsea variant: riser ID missing.",    "well.riserIdIn");
  }
}
